'use client';

import { forwardRef, useCallback } from 'react';
import type { UseTableBorderToggleConfig } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-border-toggle-button/use-table-border-toggle';
import { useTableBorderToggle } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-node/table-node/ui/table-border-toggle-button/use-table-border-toggle';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { ButtonProps } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';

export interface TableBorderToggleButtonProps extends Omit<ButtonProps, 'type'>, UseTableBorderToggleConfig {
  text?: string;
}

/**
 * Button component for toggling table border visibility.
 *
 * This component provides a user interface for showing/hiding table borders.
 * It integrates with the Tiptap table extension to modify the table's borderVisible attribute.
 *
 * @example
 * ```tsx
 * <TableBorderToggleButton
 *   editor={editor}
 *   onToggled={(visible) => console.log('Borders visible:', visible)}
 * />
 * ```
 */
export const TableBorderToggleButton = forwardRef<HTMLButtonElement, TableBorderToggleButtonProps>(
  (
    { editor: providedEditor, hideWhenUnavailable = false, onToggled, text, onClick, children, ...buttonProps },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isVisible, canToggle, label, Icon, handleToggle } = useTableBorderToggle({
      editor,
      hideWhenUnavailable,
      onToggled,
    });

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleToggle();
      },
      [handleToggle, onClick],
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        disabled={!canToggle}
        data-style="ghost"
        data-active-state="off"
        data-disabled={!canToggle}
        role="button"
        tabIndex={-1}
        aria-label={label}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </Button>
    );
  },
);

TableBorderToggleButton.displayName = 'TableBorderToggleButton';

